import { useCallback, useEffect, useState } from 'react';
import { getPortalRole } from '../app/businessContext';
import { fetchNotificationSummary } from '../services/notificationService';
import { subscribeToPurchaseOrderChanges } from '../services/purchaseOrderRealtimeService';

const NAVIGATION_NOTIFICATIONS_REFRESH_EVENT = 'seaweeds:navigation-notifications-refresh';
let cachedNavigationBadges = {};
let hasLoadedNavigationBadges = false;
let navigationBadgesRequest = null;

function useNavigationNotifications() {
  const [navigationBadges, setNavigationBadges] = useState(cachedNavigationBadges);
  const portalRole = getPortalRole();
  const shouldLoadNotifications = portalRole === 'admin' || portalRole === 'accountant';

  const loadNotifications = useCallback(async ({ force = false } = {}) => {
    if (!shouldLoadNotifications) {
      cachedNavigationBadges = {};
      hasLoadedNavigationBadges = false;
      setNavigationBadges({});
      return;
    }

    if (hasLoadedNavigationBadges && !force) {
      setNavigationBadges((currentBadges) =>
        areNavigationBadgesEqual(currentBadges, cachedNavigationBadges)
          ? currentBadges
          : cachedNavigationBadges
      );
      return;
    }

    try {
      if (!navigationBadgesRequest) {
        navigationBadgesRequest = fetchNotificationSummary().finally(() => {
          navigationBadgesRequest = null;
        });
      }

      const summary = await navigationBadgesRequest;
      const nextBadges = normalizeNavigationBadges(summary?.navigationBadges);

      cachedNavigationBadges = nextBadges;
      hasLoadedNavigationBadges = true;
      setNavigationBadges((currentBadges) =>
        areNavigationBadgesEqual(currentBadges, nextBadges) ? currentBadges : nextBadges
      );
    } catch (error) {
      if (!hasLoadedNavigationBadges) {
        setNavigationBadges({});
      }
    }
  }, [shouldLoadNotifications]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (!shouldLoadNotifications) {
      return undefined;
    }

    return subscribeToPurchaseOrderChanges(() => {
      loadNotifications({ force: true });
    });
  }, [loadNotifications, shouldLoadNotifications]);

  useEffect(() => {
    if (!shouldLoadNotifications || typeof window === 'undefined') {
      return undefined;
    }

    function handleRefreshRequest() {
      loadNotifications({ force: true });
    }

    window.addEventListener(NAVIGATION_NOTIFICATIONS_REFRESH_EVENT, handleRefreshRequest);

    return () => {
      window.removeEventListener(NAVIGATION_NOTIFICATIONS_REFRESH_EVENT, handleRefreshRequest);
    };
  }, [loadNotifications, shouldLoadNotifications]);

  return navigationBadges;
}

export function refreshNavigationNotifications() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(NAVIGATION_NOTIFICATIONS_REFRESH_EVENT));
}

function normalizeNavigationBadges(value) {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((badges, [key, count]) => {
    const normalizedCount = Number(count || 0);

    if (normalizedCount > 0) {
      badges[key] = normalizedCount;
    }

    return badges;
  }, {});
}

function areNavigationBadgesEqual(firstBadges, secondBadges) {
  const firstKeys = Object.keys(firstBadges || {});
  const secondKeys = Object.keys(secondBadges || {});

  if (firstKeys.length !== secondKeys.length) {
    return false;
  }

  return firstKeys.every((key) => Number(firstBadges[key] || 0) === Number(secondBadges[key] || 0));
}

export default useNavigationNotifications;
